import React, { useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Flex,
  Button,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useToasts } from 'react-toast-notifications';
import { useQuery } from 'react-query';
import {
  getAllServiceSettings,
  deleteServiceSettingById,
  createServiceSetting,
  updateServiceSetting,
} from '../api/service';
import DeleteServiceSettingModal from '../components/deleteServiceSettingModal.component.js';
import ServiceSettingCreateAndEditModal from '../components/serviceSettingCreateAndEditModal.component.js';
import { getApiErrorMessage } from '../utils/apiError';

const ServiceSettingPage = () => {
  const { data, error, isLoading, refetch } = useQuery(
    'serviceSettings',
    getAllServiceSettings,
    {
      refetchOnWindowFocus: false,
    }
  );
  const [selectedServiceSetting, setSelectedServiceSetting] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { addToast } = useToasts();

  const handleDelete = async id => {
    try {
      await deleteServiceSettingById(id);
      await refetch();
      addToast('Service setting deleted successfully', {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (error) {
      console.error(`Error deleting service setting ${id}:`, error);
      addToast(`Error deleting service setting: ${getApiErrorMessage(error)}`, {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedServiceSetting(null);
    }
  };

  const handleSave = async serviceSummaryData => {
    try {
      if (serviceSummaryData.id) {
        // ID varsa güncelleme yap
        await updateServiceSetting(serviceSummaryData.id, serviceSummaryData);
        addToast('Service summary updated successfully', {
          appearance: 'success',
          autoDismiss: true,
        });
      } else {
        // ID yoksa yeni oluştur
        await createServiceSetting(serviceSummaryData);
        addToast('Service summary added successfully', {
          appearance: 'success',
          autoDismiss: true,
        });
      }
      await refetch();
    } catch (error) {
      console.error('Error saving service summary:', error);
      addToast(`Error saving service summary: ${getApiErrorMessage(error)}`, {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsEditModalOpen(false);
      setSelectedServiceSetting(null);
    }
  };

  if (isLoading) {
    return (
      <Box
        w="100%"
        h="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box w="100%" display="flex" justifyContent="center" p={5}>
        <Text>An error occurred: {getApiErrorMessage(error)}</Text>
      </Box>
    );
  }

  return (
    <Box
      w="100%"
      h="100%"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      p={4}
    >
      <Box flex="1" display="flex" flexDirection="column" w="100%">
        <TableContainer
          flex="1"
          overflowY="auto"
          maxHeight="calc(100vh - 220px)"
          w="100%"
        >
          <Table variant="simple" colorScheme="gray" size="sm" w="100%">
            <Thead position="sticky" top={0} zIndex={1} bg="white">
              <Tr>
                <Th px={4} py={2}>
                  Parent
                </Th>
                <Th px={4} py={2}>
                  Instrument
                </Th>
                <Th px={4} py={2}>
                  Serial Number
                </Th>
                <Th px={4} py={2}>
                  Brand
                </Th>
                <Th px={4} py={2}>
                  Model
                </Th>
                <Th px={4} py={2}>
                  Period
                </Th>
                <Th px={4} py={2}>
                  Action
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {data && data.length > 0 ? (
                data.map(serviceSetting => (
                  <Tr key={serviceSetting.id} height="40px">
                    <Td px={4} py={2}>
                      {serviceSetting.parent}
                    </Td>
                    <Td px={4} py={2}>
                      {serviceSetting.instrument}
                    </Td>
                    <Td px={4} py={2}>
                      {serviceSetting.serialNumber}
                    </Td>
                    <Td px={4} py={2}>
                      {serviceSetting.brand}
                    </Td>
                    <Td px={4} py={2}>
                      {serviceSetting.model}
                    </Td>
                    <Td px={4} py={2}>
                      {serviceSetting.period}
                    </Td>
                    <Td px={4} py={2}>
                      <Flex align="center">
                        <Text
                          as="span"
                          mr={2}
                          cursor="pointer"
                          onClick={() => {
                            setIsEditModalOpen(true);
                            setSelectedServiceSetting(serviceSetting);
                          }}
                        >
                          Edit
                        </Text>
                        <Text as="span" mx={2}>
                          |
                        </Text>
                        <Text
                          as="span"
                          cursor="pointer"
                          onClick={() => {
                            setIsDeleteModalOpen(true);
                            setSelectedServiceSetting(serviceSetting);
                          }}
                        >
                          Delete
                        </Text>
                      </Flex>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={7}>
                    <Text textAlign="center">No service settings found.</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
        <Box mt={3} mb={4}>
          <Flex justifyContent="flex-end" gap={2}>
            <Button size="sm" colorScheme="gray">
              Export
            </Button>
            <Button size="sm" colorScheme="gray">
              Import
            </Button>
            <Button
              onClick={() => {
                setIsEditModalOpen(true);
                setSelectedServiceSetting(null);
              }}
              size="sm"
              colorScheme="gray"
            >
              Add Service Setting
            </Button>
          </Flex>
        </Box>
      </Box>

      {isDeleteModalOpen && selectedServiceSetting && (
        <DeleteServiceSettingModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onDelete={() => handleDelete(selectedServiceSetting.id)}
        />
      )}

      {isEditModalOpen && (
        <ServiceSettingCreateAndEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          serviceSetting={selectedServiceSetting}
          onSave={handleSave}
        />
      )}
    </Box>
  );
};

export default ServiceSettingPage;
